import { type JanusCtx, type Row, ecId, handBack, itemField, record, requiredInt, upper, definitionNotHeld } from '../context.js';
import { phpJsonInt } from '../../compile/php-json-values.js';
import { distinctUuids } from '../../reactions/layer-list-ids.js';
import { discernVariableTags } from '../discern-variable-tags.js';
import { OVERRUN_ABILITY_ID } from '../../compile/creature-overrun-charges.js';

// ft_caster_charge_bucket_types(): the same buckets adjust_charges.php scans via layer_entries().
export const ABILITY_CHARGE_BUCKETS = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability',
  'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta',
  'sanguine', 'ability_choice', 'innate_spell_per_long_rest'] as const;

export function abilityEntryId(value: unknown): string {
  if (typeof value === 'string') return upper(value);
  const entry = record(value);
  if (!entry) return '';
  const nested = entry.item_arr;
  return upper(entry.ability_id ?? (typeof nested === 'string' ? nested : null)
    ?? itemField(record(nested), 'item_id') ?? itemField(entry, 'item_id'));
}

const MAX_MODIFIER_LAYERS = ['increase_max_charges_of_ability', 'decrease_max_charges_of_ability'] as const;

/** process_ability_charges(): first nonempty curated slot, without provider-authored grants. */
function rawModifierBucket(caster: Row, bucket: string): unknown[] {
  const abilities = record(record(record(caster.computed)?.creature)?.abilities);
  for (const slots of [caster, record(caster.item_layer_types), abilities]) {
    const entries = slots?.[bucket];
    if (Array.isArray(entries) && entries.length) return entries;
  }
  return [];
}

/** Rebuild an omitted ceiling with the action's formula readers, including preserved shapeshift modifiers. */
function definitionMaxWithoutModifiers(ctx: JanusCtx, caster: Row, definition: Row, id: string): number {
  const raw = ctx.read.value(definition, 'ability_charges');
  let max = discernVariableTags(ctx, raw, caster, { reason: 'adjust_charges:maximum-charges-not-held', integer: true });
  const fold = (value: unknown): void => {
    const entry = record(value), source = record(entry?.item_arr) ?? entry;
    const layers = record(source?.item_layer_types);
    const carrier = source && layers && Object.keys(layers).length ? source : ctx.read.definition(abilityEntryId(value));
    if (!carrier) handBack('adjust_charges:modifier-carrier-not-held');
    for (const layer of MAX_MODIFIER_LAYERS) {
      if (!distinctUuids(ctx.read.entries(carrier, layer)).some(target => upper(target) === id)) continue;
      const increasing = layer === 'increase_max_charges_of_ability';
      const qty = ctx.read.value(carrier, increasing ? 'increase_max_charges_by_qty' : 'decrease_max_charges_by_qty') ?? 1;
      const formula = typeof qty === 'string' || typeof qty === 'number' ? qty : phpJsonInt(qty);
      max += discernVariableTags(ctx, formula, caster, { reason: 'adjust_charges:max-charge-modifier', integer: true })
        * (increasing ? 1 : -1);
    }
  };
  for (const bucket of ['passive_ability', 'feat']) for (const value of rawModifierBucket(caster, bucket)) fold(value);
  const preserved = record(record(caster.computed)?.creature)?.shapeshift_charge_mods;
  if (Array.isArray(preserved)) for (const value of preserved) {
    if (record(value)) fold(value);
  }
  return requiredInt(max, 'adjust_charges:maximum-charges-not-held');
}

export interface AbilityChargeEntry { entry: Row | null; current: number; max: number }

/** The writer and saved-row installer share every projection and its exact path. */
export function* rowAbilityChargeBuckets(row: Row): Generator<{ value: unknown; path: string[] }> {
  function* bucket(slot: Row, key: string, path: string[]) {
    const value = slot[key];
    if (Array.isArray(value)) for (const [index, entry] of value.entries()) yield { value: entry, path: [...path, String(index)] };
    else yield { value, path };
  }
  const abilities = record(record(record(row.computed)?.creature)?.abilities);
  if (abilities) for (const key of Object.keys(abilities)) yield* bucket(abilities, key, ['computed', 'creature', 'abilities', key]);
  const castable = record(record(record(row.computed)?.creature)?.castable);
  if (castable && 'innate_spell_per_long_rest' in castable)
    yield* bucket(castable, 'innate_spell_per_long_rest', ['computed', 'creature', 'castable', 'innate_spell_per_long_rest']);
  const buckets = [...ABILITY_CHARGE_BUCKETS, 'at_will_spell', 'spells_known'];
  const layers = record(row.item_layer_types);
  for (const key of buckets) {
    if (layers && key in layers) yield* bucket(layers, key, ['item_layer_types', key]);
    if (key in row) yield* bucket(row, key, [key]);
  }
}

/** Read-only plan: only a matching pool's leaves and its stale cache signatures. */
export function rowAbilityChargeUpdates(row: Row, itemId: string, current: number): { set: { path: string[]; value: unknown }[]; unset: string[][] } {
  const set: { path: string[]; value: unknown }[] = [], unset: string[][] = [];
  for (const { value, path } of rowAbilityChargeBuckets(row)) {
    if (abilityEntryId(value) !== upper(itemId)) continue;
    const entry = record(value);
    if (!entry) set.push({ path, value: { item_id: value, charges: { current } } });
    else {
      set.push(record(entry.charges) ? { path: [...path, 'charges', 'current'], value: current }
        : { path: [...path, 'charges'], value: { current } });
      if ('ability_charges' in entry) unset.push([...path, 'ability_charges']);
    }
  }
  if (set.length) {
    const computed = record(row.computed), creature = record(computed?.creature), cache = record(computed?._cache);
    if (creature && upper(itemId) === OVERRUN_ABILITY_ID)
      set.push({ path: ['computed', 'creature', 'overrun_charges_remaining'], value: current });
    for (const key of ['pl_sig', 'pl_sig_b']) if (cache && key in cache) unset.push(['computed', '_cache', key]);
  }
  return { set, unset };
}

/** Patch every compiled projection of one charge pool (turns, casts and attacks). */
export function setRowAbilityCharges(row: Row, itemId: string, current: number): boolean {
  const patch = rowAbilityChargeUpdates(row, itemId, current);
  const parent = (path: string[]): Row => path.slice(0, -1).reduce<Row>((node, key) => node[key] as Row, row);
  for (const path of patch.unset) delete parent(path)[path[path.length - 1]!];
  for (const { path, value } of patch.set) parent(path)[path[path.length - 1]!] = value;
  return patch.set.length > 0;
}

/** Internal charge-writer read: preserve the persisted before-value even when adjust_charges must clamp it. */
export function rawAbilityChargeEntry(ctx: JanusCtx, caster: Row, itemId: string): AbilityChargeEntry {
  const computed = record(record(record(caster.computed)?.creature)?.abilities);
  const root = record(caster.item_layer_types);
  if (!computed && !root) handBack('adjust_charges:caster-ability-buckets-not-held');
  const id = upper(itemId);
  for (const bucket of ABILITY_CHARGE_BUCKETS) {
    for (const value of ctx.read.entries(caster, bucket)) {
      if (abilityEntryId(value) !== id) continue;
      const entry = record(value);
      const charges = record(entry?.charges);
      const definition = ctx.read.definition(id);
      if (!definition) definitionNotHeld('adjust_charges:definition-not-held', id);
      // An omitted count proves nothing about the persisted row; never assume a full pool.
      const snapshot = requiredInt(charges?.current ?? entry?.ability_charges, 'adjust_charges:current-charges-not-held');
      // adjust_charges() re-queries the persisted row before every delta. The
      // browser's prior writes in this same resolution are held in chargesChanged.
      const holder = ecId(caster);
      const prior = [...ctx.chargesChanged].reverse().find(change => change.holder === holder && change.item_id === id);
      const current = prior ? prior.charges : snapshot;
      // The compiled max already folds process_ability_charges() (passive / feat increase_ / decrease_max_charges_of_ability, with
      // variable tags) and shapeshift-preserved mods. Rebuild an omitted max from the same held formula inputs.
      const compiledMax = charges?.max ?? entry?.max_ability_charges;
      const max = compiledMax != null
        ? requiredInt(compiledMax, 'adjust_charges:maximum-charges-not-held')
        : definitionMaxWithoutModifiers(ctx, caster, definition as Row, id);
      return { entry, current, max };
    }
  }
  handBack('adjust_charges:ability-entry-not-held');
}

/** Strict read for affordability, innate spells and attacks; only the charge writer normalizes invalid state. */
export function abilityChargeEntry(ctx: JanusCtx, caster: Row, itemId: string): AbilityChargeEntry {
  const pool = rawAbilityChargeEntry(ctx, caster, itemId);
  if (pool.current < 0 || pool.max < 1 || pool.current > pool.max) handBack('adjust_charges:invalid-charge-state');
  return pool;
}
